import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('wos_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('wos_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me')
};

// Company APIs
export const companyAPI = {
  getAll: () => api.get('/companies'),
  getAllForOwner: (includeArchived = false) => api.get(`/owner/all-companies?include_archived=${includeArchived}`),
  getTransferTargets: () => api.get('/companies/transfer-targets'),
  getOne: (id) => api.get(`/companies/${id}`),
  getStats: (id) => api.get(`/companies/${id}/stats`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  updateFeatureFlags: (id, flags) => api.put(`/companies/${id}/feature-flags`, flags),
  archive: (id) => api.put(`/companies/${id}/archive`),
  restore: (id) => api.put(`/companies/${id}/restore`)
};

// Department APIs
export const departmentAPI = {
  getAll: (companyId) => api.get(`/companies/${companyId}/departments`),
  create: (companyId, data) => api.post(`/companies/${companyId}/departments`, data)
};

// Employee APIs
export const employeeAPI = {
  getAll: (companyId) => api.get(`/companies/${companyId}/employees`),
  getOne: (id) => api.get(`/employees/${id}`),
  create: (companyId, data) => api.post(`/companies/${companyId}/employees`, data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  transfer: (data) => api.post('/employees/transfer', data),
  getTransferHistory: (id) => api.get(`/employees/${id}/transfer-history`)
};

// Shift APIs
export const shiftAPI = {
  getAll: (companyId) => api.get(`/companies/${companyId}/shifts`),
  create: (companyId, data) => api.post(`/companies/${companyId}/shifts`, data)
};

// Leave APIs
export const leaveAPI = {
  getTypes: (companyId) => api.get(`/companies/${companyId}/leave-types`),
  createType: (companyId, data) => api.post(`/companies/${companyId}/leave-types`, data),
  getRequests: (companyId, status) => {
    const params = status ? `?status=${status}` : '';
    return api.get(`/companies/${companyId}/leaves${params}`);
  },
  createRequest: (data) => api.post('/leaves', data),
  approve: (id) => api.put(`/leaves/${id}/approve`),
  reject: (id, reason) => api.put(`/leaves/${id}/reject?reason=${encodeURIComponent(reason || '')}`),
  // Leave Balances
  getBalances: (companyId, employeeId, year) => {
    const params = [];
    if (employeeId) params.push(`employee_id=${employeeId}`);
    if (year) params.push(`year=${year}`);
    const query = params.length ? `?${params.join('&')}` : '';
    return api.get(`/companies/${companyId}/leave-balances${query}`);
  },
  getMyBalances: (employeeId, year) => {
    const query = year ? `?year=${year}` : '';
    return api.get(`/employees/${employeeId}/leave-balances${query}`);
  },
  createBalance: (employeeId, data) => api.post(`/employees/${employeeId}/leave-balances`, data),
  adjustBalance: (balanceId, data) => api.put(`/leave-balances/${balanceId}/adjust`, data),
  initializeBalances: (companyId, year) => {
    const query = year ? `?year=${year}` : '';
    return api.post(`/companies/${companyId}/leave-balances/initialize${query}`);
  }
};

// Attendance APIs
export const attendanceAPI = {
  getAll: (companyId, date, employeeId) => {
    let params = [];
    if (date) params.push(`date=${date}`);
    if (employeeId) params.push(`employee_id=${employeeId}`);
    const queryString = params.length ? `?${params.join('&')}` : '';
    return api.get(`/companies/${companyId}/attendance${queryString}`);
  },
  getToday: () => api.get('/attendance/today'),
  clockIn: (data) => api.post('/attendance/clock-in', data),
  clockOut: (data) => api.post('/attendance/clock-out', data)
};

// Shift Change APIs
export const shiftChangeAPI = {
  getAll: (companyId, status) => {
    const params = status ? `?status=${status}` : '';
    return api.get(`/companies/${companyId}/shift-changes${params}`);
  },
  create: (data) => api.post('/shift-changes', data),
  approve: (id) => api.put(`/shift-changes/${id}/approve`),
  reject: (id, reason) => api.put(`/shift-changes/${id}/reject?reason=${encodeURIComponent(reason || '')}`)
};

// Payroll APIs
export const payrollAPI = {
  getConfig: (companyId) => api.get(`/companies/${companyId}/payroll-config`),
  updateConfig: (companyId, data) => api.put(`/companies/${companyId}/payroll-config`, data),
  getRecords: (companyId, periodStart, periodEnd) => {
    let params = [];
    if (periodStart) params.push(`period_start=${periodStart}`);
    if (periodEnd) params.push(`period_end=${periodEnd}`);
    const queryString = params.length ? `?${params.join('&')}` : '';
    return api.get(`/companies/${companyId}/payroll${queryString}`);
  },
  generate: (companyId, periodStart, periodEnd) => 
    api.post(`/companies/${companyId}/payroll/generate?period_start=${periodStart}&period_end=${periodEnd}`)
};

// Dashboard APIs
export const dashboardAPI = {
  getOwnerDashboard: () => api.get('/dashboard/owner'),
  getCompanyDashboard: (companyId) => api.get(`/dashboard/company/${companyId}`)
};

// Audit APIs
export const auditAPI = {
  getLogs: (companyId, eventType, limit = 100) => {
    let params = [`limit=${limit}`];
    if (eventType) params.push(`event_type=${eventType}`);
    return api.get(`/companies/${companyId}/audit-logs?${params.join('&')}`);
  }
};

// Reports APIs
export const reportsAPI = {
  getAuditReport: (params) => {
    const queryParams = new URLSearchParams();
    if (params.company_id) queryParams.append('company_id', params.company_id);
    if (params.employee_id) queryParams.append('employee_id', params.employee_id);
    if (params.event_type) queryParams.append('event_type', params.event_type);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.limit) queryParams.append('limit', params.limit);
    return api.get(`/reports/audit?${queryParams.toString()}`);
  },
  exportAuditCSV: (queryString) => api.get(`/reports/audit/csv?${queryString}`, { responseType: 'blob' }),
  getGpsExceptions: (companyId, startDate, endDate) => 
    api.get(`/reports/gps-exceptions?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`),
  getDeviceIpReport: (companyId, startDate, endDate) => 
    api.get(`/reports/device-ip?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`),
  getOvertime: (companyId, startDate, endDate, departmentId) => {
    let url = `/reports/overtime?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`;
    if (departmentId) url += `&department_id=${departmentId}`;
    return api.get(url);
  },
  getOvertimeReport: (companyId, startDate, endDate, departmentId) => {
    let url = `/reports/overtime?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`;
    if (departmentId) url += `&department_id=${departmentId}`;
    return api.get(url);
  },
  getOvertimeSummary: (companyId, startDate, endDate) => 
    api.get(`/reports/overtime/summary?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`),
  getTasks: (companyId, startDate, endDate, employeeId) => {
    let url = `/reports/tasks?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`;
    if (employeeId) url += `&employee_id=${employeeId}`;
    return api.get(url);
  },
  getTaskReport: (companyId, startDate, endDate, employeeId) => {
    let url = `/reports/tasks?company_id=${companyId}&start_date=${startDate}&end_date=${endDate}`;
    if (employeeId) url += `&employee_id=${employeeId}`;
    return api.get(url);
  }
};

// Manager Dashboard API
export const managerDashboardAPI = {
  getTeam: () => api.get('/manager/team'),
  getTeamDashboard: () => api.get('/manager/team/dashboard'),
  getApprovals: () => api.get('/manager/approvals'),
  getTeamAttendanceAudit: (startDate, endDate) => 
    api.get(`/manager/team-attendance-audit?start_date=${startDate}&end_date=${endDate}`),
  getManagers: (companyId) => api.get(`/companies/${companyId}/managers`)
};

// Shift Assignment APIs
export const shiftAssignmentAPI = {
  getAll: (companyId, employeeId) => {
    let url = `/companies/${companyId}/shift-assignments`;
    if (employeeId) url += `?employee_id=${employeeId}`;
    return api.get(url);
  },
  create: (companyId, data) => api.post(`/companies/${companyId}/shift-assignments`, data),
  bulkCreate: (companyId, data) => api.post(`/companies/${companyId}/shift-assignments/bulk`, data),
  delete: (assignmentId) => api.delete(`/shift-assignments/${assignmentId}`)
};

// Task APIs
export const taskAPI = {
  getMyTasks: () => api.get('/tasks'),
  create: (data) => api.post('/tasks', data),
  update: (taskId, data) => api.put(`/tasks/${taskId}`, data),
  delete: (taskId) => api.delete(`/tasks/${taskId}`),
  getActiveTask: () => api.get('/tasks/active'),
  switchTask: (taskId) => api.post('/tasks/switch', { task_id: taskId }),
  stopTask: () => api.post('/tasks/stop'),
  getLogs: (startDate, endDate) => {
    let url = '/tasks/logs';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  }
};

// Holiday APIs
export const holidayAPI = {
  getAll: (companyId, year) => {
    let url = `/companies/${companyId}/holidays`;
    if (year) url += `?year=${year}`;
    return api.get(url);
  },
  create: (companyId, data) => api.post(`/companies/${companyId}/holidays`, data),
  update: (holidayId, data) => api.put(`/holidays/${holidayId}`, data),
  delete: (holidayId) => api.delete(`/holidays/${holidayId}`),
  check: (companyId, date) => api.get(`/companies/${companyId}/holidays/check/${date}`)
};

// Schedule Adjustment APIs
export const scheduleAdjustmentAPI = {
  getMine: (status) => {
    let url = '/schedule-adjustments';
    if (status) url += `?status=${status}`;
    return api.get(url);
  },
  create: (data) => api.post('/schedule-adjustments', data),
  managerApprove: (id) => api.put(`/schedule-adjustments/${id}/manager-approve`),
  hrApprove: (id) => api.put(`/schedule-adjustments/${id}/hr-approve`),
  reject: (id, reason) => api.put(`/schedule-adjustments/${id}/reject?reason=${encodeURIComponent(reason || '')}`)
};

// Time Adjustment APIs
export const timeAdjustmentAPI = {
  getMine: (status) => {
    let url = '/time-adjustments';
    if (status) url += `?status=${status}`;
    return api.get(url);
  },
  create: (data) => api.post('/time-adjustments', data),
  approve: (id) => api.put(`/time-adjustments/${id}/approve`),
  reject: (id, reason) => api.put(`/time-adjustments/${id}/reject?reason=${encodeURIComponent(reason || '')}`)
};

// HR Override API
export const hrOverrideAPI = {
  unlockRequest: (requestType, requestId) => api.put(`/requests/${requestType}/${requestId}/unlock`)
};

// OT Request APIs
export const otRequestAPI = {
  // Employee: Get my OT requests
  getMine: (status) => {
    let url = '/ot-requests';
    if (status) url += `?status=${status}`;
    return api.get(url);
  },
  // Employee: Create OT request
  create: (data) => api.post('/ot-requests', data),
  // Manager/Admin/Owner: Approve OT request
  approve: (requestId, approvedHours) => {
    const body = approvedHours ? { approved_hours: approvedHours } : {};
    return api.put(`/ot-requests/${requestId}/approve`, body);
  },
  // Manager/Admin/Owner: Reject OT request
  reject: (requestId, reason) => api.put(`/ot-requests/${requestId}/reject?reason=${encodeURIComponent(reason || '')}`),
  // Admin/Owner: Get all company OT requests
  getCompanyOT: (companyId, status, startDate, endDate) => {
    let url = `/companies/${companyId}/ot-requests`;
    const params = [];
    if (status) params.push(`status=${status}`);
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  }
};

// Owner Dashboard Widgets API
export const ownerWidgetsAPI = {
  getWidgets: () => api.get('/owner/widgets'),
  getPayrollForecast: (period) => api.get(`/owner/payroll-forecast?forecast_period=${period}`),
  getPayrollTrends: (months = 6) => api.get(`/owner/payroll-trends?months=${months}`)
};

// Owner Approval Inbox API (for manager/admin leaves)
export const ownerApprovalAPI = {
  getInbox: (status = 'pending') => api.get(`/owner/approval-inbox?status=${status}`),
  getStats: () => api.get('/owner/approval-inbox/stats'),
  approveLeave: (leaveId) => api.put(`/leaves/${leaveId}/approve`),
  rejectLeave: (leaveId, reason = '') => api.put(`/leaves/${leaveId}/reject?reason=${encodeURIComponent(reason)}`)
};

// Compensation Change API
export const compensationAPI = {
  getChanges: (companyId, status) => api.get(`/companies/${companyId}/compensation-changes${status ? `?status=${status}` : ''}`),
  createChange: (companyId, data) => api.post(`/companies/${companyId}/compensation-changes`, data),
  getOwnerApprovals: (status = 'pending') => api.get(`/owner/compensation-approvals?status=${status}`),
  approveChange: (changeId) => api.put(`/compensation-changes/${changeId}/approve`),
  rejectChange: (changeId, reason = '') => api.put(`/compensation-changes/${changeId}/reject?reason=${encodeURIComponent(reason)}`)
};

// Pay Item API
export const payItemAPI = {
  getCatalog: (companyId) => api.get(`/companies/${companyId}/pay-item-catalog`),
  addToCatalog: (companyId, data) => api.post(`/companies/${companyId}/pay-item-catalog`, data),
  removeFromCatalog: (itemId) => api.delete(`/pay-item-catalog/${itemId}`),
  getEmployeeItems: (employeeId) => api.get(`/employees/${employeeId}/pay-items`),
  addToEmployee: (employeeId, data) => api.post(`/employees/${employeeId}/pay-items`, data),
  removeFromEmployee: (itemId) => api.delete(`/employee-pay-items/${itemId}`)
};

// Government Contribution Config API
export const govContributionAPI = {
  getConfigs: (companyId) => api.get(`/companies/${companyId}/gov-contributions`),
  requestChange: (companyId, configType, proposedChanges, effectiveDate, reason) => 
    api.post(`/companies/${companyId}/gov-contributions/change-request?config_type=${configType}&effective_date=${effectiveDate}&reason=${encodeURIComponent(reason)}`, proposedChanges),
  getOwnerApprovals: (status = 'pending') => api.get(`/owner/gov-config-approvals?status=${status}`),
  approveChange: (changeId) => api.put(`/gov-config-changes/${changeId}/approve`)
};

// Payslip API
export const payslipAPI = {
  getMyPayslips: () => api.get('/payslips/my'),
  getPayslip: (payslipId) => api.get(`/payslips/${payslipId}`),
  downloadPdf: (payslipId) => api.get(`/payslips/${payslipId}/pdf`, { responseType: 'blob' }),
  getCompanyPayslips: (companyId, periodStart, periodEnd) => {
    let url = `/companies/${companyId}/payslips`;
    const params = [];
    if (periodStart) params.push(`period_start=${periodStart}`);
    if (periodEnd) params.push(`period_end=${periodEnd}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
  generatePayslips: (companyId, periodStart, periodEnd, payDate) => 
    api.post(`/companies/${companyId}/payroll/generate-payslips?period_start=${periodStart}&period_end=${periodEnd}&pay_date=${payDate}`)
};

// Payroll with Holidays API
export const payrollHolidayAPI = {
  generate: (companyId, periodStart, periodEnd) => 
    api.post(`/companies/${companyId}/payroll/generate-with-holidays?period_start=${periodStart}&period_end=${periodEnd}`)
};

// Seed Data
export const seedAPI = {
  seedDemo: () => api.post('/seed-demo-data')
};

export default api;
